import { BaseLayout } from "../layouts";
import { Button, Typography } from "@mui/material";
import {
  ConsentContext,
  Container,
  InlineLink,
  InternalInlineLink,
  NextPageWithLayout,
} from "@akte-mord/common";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../next-i18next.config";
import React from "react";
import type { GetStaticProps } from "next";

const PrivacyPolicy: NextPageWithLayout = () => {
  const { openPreferences } = React.useContext(ConsentContext);

  return (
    <Container>
      <Typography variant="h1">Datenschutzerklärung</Typography>
      <Typography variant="body1" paragraph>
        Wir freuen uns über dein Interesse an unserem Unternehmen und unseren Produkten. Der Schutz
        deiner personenbezogenen Daten ist uns ein wichtiges Anliegen. Mit dieser Datenschutzerklärung
        möchten wir dich über die Art, den Zweck, die Verwendung und den Schutz personenbezogener Daten
        durch die Bischoff, Kauffmann & Otani GbR beim Betrieb des Webshops auf der Internetseite unter
        „www.akte-mord.de“ (im Folgenden „Webshop“ genannt) informieren.
      </Typography>

      <Typography variant="body1" paragraph>
        Die Verarbeitung deiner personenbezogenen Daten im Webshop erfolgt stets im Einklang mit der
        DSGVO und in Übereinstimmung mit den für uns geltenden nationalen Datenschutzbestimmungen. Die
        Datenverarbeitung erfolgt entweder auf einer gesetzlichen Grundlage oder aber wir holen deine
        Einwilligung ein.
      </Typography>

      <Typography variant="body1" paragraph>
        Wir haben zum Schutz deiner Daten und deren Vertraulichkeit, Verfügbarkeit und Integrität
        angemessene technische und organisatorische Maßnahmen ergriffen und die von uns beauftragten
        Dienstleister daraufhin überprüft und vertraglich verpflichtet, ein vergleichbar angemessenes
        Schutzniveau sicherzustellen.
      </Typography>

      <Typography variant="h2">1. Begriffsbestimmungen</Typography>
      <Typography variant="body1" paragraph>
        Unsere Datenschutzerklärung verwendet die Begriffe, wie sie in der Datenschutz-Grundverordnung
        definiert sind (Artikel 4 DSGVO).
      </Typography>

      <Typography variant="h2">2. Verantwortung</Typography>
      <Typography variant="body1" paragraph>
        Verantwortlich für die Erhebung, Verarbeitung und Nutzung deiner personenbezogenen Daten im Sinne
        der Datenschutz-Grundverordnung ist die Bischoff, Kauffmann & Otani GbR, Hohenzollerndamm 152,
        14199 Berlin, Deutschland.
      </Typography>

      <Typography variant="body1" paragraph>
        Die Bischoff, Kauffmann & Otani GbR wird durch die geschäftsführenden Gesellschafter Florian
        Norbert Bischoff, Jens Kauffmann und Takeshi Otani vertreten und ist wie folgt zu erreichen:
        Tel.: +49 (0) 30 25 29 21 51, E-Mail:{" "}
        <InlineLink href="mailto: mail@akte-mord.de">mail@akte-mord.de</InlineLink>, Website:{" "}
        <InternalInlineLink href="/">www.akte-mord.de</InternalInlineLink>.
      </Typography>

      <Typography variant="h2">3. Datenschutzbeauftragter</Typography>
      <Typography variant="body1" paragraph>
        Die Bischoff, Kauffmann & Otani GbR hat keinen Datenschutzbeauftragten bestellt und ist dazu
        gesetzlich auch nicht verpflichtet. Da Datenschutz für uns ein wichtiges Anliegen und damit
        Chefsache ist, kannst du dich in Sachen Datenschutz gerne an die Geschäftsführer wenden, die dein
        Anliegen mit hoher Priorität und kompetent bearbeiten.
      </Typography>

      <Typography variant="h2">4. Datenverarbeitungen</Typography>
      <Typography variant="body1" paragraph>
        Beim Betrieb des Webshops werden deine personenbezogenen Daten von uns verarbeitet, um dich über
        uns und unsere Produkte zu informieren, dir den Kauf unserer Produkte zu ermöglichen,
        Hilfestellung und Support zu leisten und, falls von dir gewünscht, zu sonstigen Zwecken mit dir
        in Kontakt zu treten. Die Verarbeitung personenbezogener Daten findet dabei auch statt, um den
        Webshop und zugehörige Dienste überhaupt verfügbar zu machen und den Webshop sicher und effizient
        zu betreiben. Deine personenbezogene Daten werden von uns ausschließlich für diese Zwecke
        verarbeitet.
      </Typography>

      <Typography variant="body1" paragraph>
        Unser Webshop verwendet Cookies und Tracking-Technologien. Bei Cookies handelt es sich um kleine
        Textdateien, die auf deinem Gerät gespeichert werden und durch die bei deinem nächsten Besuch
        bereits getätigte Einstellungen und andere Änderungen, die du vorgenommen hast, rekonstruiert
        werden.
      </Typography>

      <Typography variant="body1" paragraph>
        Wir unterscheiden dabei zwischen essenziellen und nicht-essenziellen Cookies. Du hast
        grundsätzlich die Möglichkeit, in deinem Browser das Setzen von Cookies zu blockieren und bereits
        gesetzte Cookies zu löschen. Dann kann es sein, dass bestimmte Features auf der Seite nicht mehr
        oder nur noch eingeschränkt funktionieren.
      </Typography>

      <Typography variant="body1" paragraph>
        Beim Besuch unserer Seite geben wir dir die Möglichkeit, deine Datenschutz-Einstellungen
        festzulegen und dabei der Verwendung von nicht-essenziellen Cookies und Tracking-Technologien
        zuzustimmen oder diese abzulehnen. Deine Einstellungen und Einwilligungen werden in einem
        technisch notwendigen Cookie (borlabs-cookie) gespeichert. Möchtest du deine Einwilligungen
        widerrufen, kannst du einfach das besagte Cookie in deinem Browser löschen. Wenn du die Website
        neu lädst, wirst du erneut nach deiner Cookie-Einwilligung gefragt.
      </Typography>

      <Typography variant="body1" paragraph>
        Alternativ kannst du deine Einwilligungen und Datenschutz-Einstellungen auch durch einen Klick
        auf den nachfolgenden Button verwalten:
      </Typography>

      <Button sx={{ mt: 2, mb: 3 }} color="secondary" variant="outlined" onClick={openPreferences}>
        Datenschutz-Einstellungen verwalten
      </Button>

      <Typography variant="body1" paragraph>
        Wir speichern personenbezogene Daten nur so lange, bis der Zweck Ihrer Speicherung erfüllt ist
        oder dies durch gesetzliche Aufbewahrungspflichten vorgeschrieben ist. Ist der Zweck der
        Speicherung erfüllt bzw. entfallen und sind alle gesetzlichen Aufbewahrungsfristen ausgelaufen,
        werden die gespeicherten Daten von uns gelöscht.
      </Typography>

      <Typography variant="h3">4.1 Sicherheit und Verfügbarkeit des Webshops</Typography>
      <Typography variant="body1" paragraph>
        Um dir den Webshop verfügbar zu machen, grundlegenden Funktionen und den einwandfreien Betrieb zu
        ermöglichen, ist es technisch erforderlich, personenbezogene Daten von dir zu verarbeiten. So
        werden z.B. bei der Verbindung zwischen Endgerät und den Servern, auf dem der Webshop sowie
        unsere Medien (z. B. Bilder) gehostet werden, automatisch die IP-Adresse des genutzten
        Endgerätes, die Kennung des genutzten Endgerätes, das verwendete Betriebssystem und der Browser
        verarbeitet, um einen sicheren Verbindungsaufbau zu gewährleisten.
      </Typography>

      <Typography variant="body1" paragraph>
        Diese Daten werden, je nachdem, von welchem Standort aus du auf unsere Angebote zugreifst, auch
        an Server in sichere Drittländer gesendet. Sichere Drittländer sind solche, denen die Europäische
        Kommission ein angemessenes Datenschutzniveau bestätigt hat und deren nationale Gesetzgebung
        einen Schutz von personenbezogenen Daten garantiert, der mit dem des EU-Rechts vergleichbar ist.
      </Typography>

      <Typography variant="body1" paragraph>
        Insbesondere werden zur grundsätzlichen Verfügbarkeit des Webshops auf dem von dir genutzten
        Endgerät auch essenzielle Cookies gesetzt.
      </Typography>

      <Typography variant="body1" paragraph>
        Dabei handelt es sich zwar um Gerätedaten, mit diesen Daten kann aber möglicherweise der Bezug zu
        dir hergestellt werden. Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 b) DSGVO
        (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Um die Sicherheit des Webshop-Betriebes zu gewährleisten, werden IP-Adressen, Gerätekennung,
        Kennungen der genutzten Endgeräte, der Betriebssysteme und der Browser, Datum und Uhrzeit des
        Besuchs, die Internetseite, von der du auf unseren Webshop gelangst (Referrer), dein Internet
        Service-Provider (ISP) und die von dir betrachteten Seiten als Logfiles verarbeitet.
      </Typography>

      <Typography variant="body1" paragraph>
        Um unseren Webshpop zuverlässig und sicher zu betreiben verwenden wir ein
        Content-Delivery-Network (CDN). Hierbei handelt es sich um ein Netzwerk aus Servern, die Inhalte
        und Medien an mehreren Orten auf der ganzen Welt zwischenspeichern. Bei Nutzung unserer Angebote
        wird eine Verbindung zwischen deinem Endgerät und den Servern unseres CDN aufgebaut.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 b) DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="h3">4.2 Erstellung von Statistiken</Typography>
      <Typography variant="h4">4.2.1 Google Analytics</Typography>
      <Typography variant="body1" paragraph>
        Sofern du wie oben beschrieben deine Einwilligung gegeben hast, verwendet unser Webshop Google
        Analytics. Dieser Dienst ermöglicht es uns, durch Erstellen von Reports die Nutzung unseres
        Webshops nachzuvollziehen und somit unser Angebot auf die Bedürfnisse der Benutzer anzupassen.
      </Typography>

      <Typography variant="body1" paragraph>
        Google Analytics setzt eigene Cookies, welche die erfassten Daten in der Regel an einen Server in
        den USA übertragen und dort speichern.
      </Typography>

      <Typography variant="body1" paragraph>
        Daten,Daten, welche von Google Analytics generiert und verarbeitet werden, umfassen die von
        deinem Gerät verwendete IP-Adresse, Datum und Uhrzeit des Besuchs, den Browsertyp und das
        Betriebssystem deines Geräts, die Internetseite, von der du auf unseren Webshop gelangst
        (Referrer), dein Internet Service-Provider (ISP) sowie die von dir betrachteten Seiten
        einschließlich der Zeit, die du auf den Seiten verbringst.
      </Typography>

      <Typography variant="body1" paragraph>
        Wir verwenden den Zusatz <code>_gat._anonymizeIp</code>, wodurch deine IP-Adresse von Google
        gekürzt und anonymisiert wird, wenn der Aufruf unseres Webshops aus einem EU-Mitgliedstaat oder
        aus einem Vertragsstaat des Abkommens über den Europäischen Wirtschaftsraum erfolgt.
      </Typography>

      <Typography variant="body1" paragraph>
        Wenn du keine Erfassung deiner Daten durch Google Analytics wünschst, findest du unter folgendem
        Link ein entsprechendes Browser-Plugin, mit dessen Hilfe du der Verarbeitung durch Google
        widersprechen kannst:{" "}
        <InlineLink
          href="https://tools.google.com/dlpage/gaoptout?hl=de"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://tools.google.com/dlpage/gaoptout?hl=de
        </InlineLink>{" "}
        Darüber hinaus kannst du das Setzen eines Cookies durch Google Analytics wie oben beschrieben
        blockieren oder deine Einwilligung zum Tracking durch Google Analytics widerrufen.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 a) DSGVO (Einwilligung).
      </Typography>

      <Typography variant="body1" paragraph>
        Betreiber des Dienstes Google Analytics ist die Google Inc., 1600 Amphitheatre Parkway, Mountain
        View, CA 94043-1351, USA.
      </Typography>

      <Typography variant="body1" paragraph>
        Nähere Informationen zu den Datenschutzbestimmungen von Google findest du unter{" "}
        <InlineLink
          href="https://policies.google.com/privacy?hl=de&gl=de"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://policies.google.com/privacy?hl=de&gl=de
        </InlineLink>
      </Typography>

      <Typography variant="h3">4.3 Einkauf im Webshop und Zahlung</Typography>
      <Typography variant="body1" paragraph>
        Zur Anbahnung, Abschluss, Abwicklung und Rückabwicklung von Kaufverträgen, einschließlich den
        daraus möglicherweise entstehender Haftungs- und Gewährleistungsansprüchen verarbeiten wir deinen
        Vor- und Zunamen, deine Rechnungs- und Lieferanschrift, deine E-Mail-Adresse, die Details zur
        Bestellung, die gewählte Zahlungsart und den Zahlungsdienstleister sowie die IP-Adresse.
      </Typography>

      <Typography variant="body1" paragraph>
        Für die Funktion des Warenkorbes werden mit Hilfe eines Cookies die Artikel gespeichert, die du
        als Kunde in deinen virtuellen Warenkorb gelegt hast (essenzielles Cookie).
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 b) DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Für die Erfüllung der mit uns geschlossenen Kaufverträge geben wir, soweit dies erforderlich ist,
        personenbezogene Daten an einen Fulfillment-Dienstleister weiter, der in unserem Auftrag den
        Versand der Ware vornimmt.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 b) DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Steuerrechtlich und handelsrechtlich aufbewahrungspflichtige Dokumente werden entsprechend der
        gesetzlichen Aufbewahrungspflichten aufbewahrt und nach Ablauf der Aufbewahrungsfrist gelöscht.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage sind die jeweiligen steuer- und handelsrechtlichen Regelungen zu
        Aufbewahrungspflichten und -fristen i. V. m. Art 6 Abs. 1 c) DSGVO (Erfüllung einer rechtlichen
        Verpflichtung).
      </Typography>

      <Typography variant="body1" paragraph>
        Für die Zahlung der Bestellungen können von dir verschiedene Zahlungsdienstleister gewählt
        werden, die dann deine personenbezogenen Daten wie folgt verarbeiten:
      </Typography>

      <Typography variant="h4">Paypal</Typography>

      <Typography variant="body1" paragraph>
        Unser Webshop bietet als Zahlungsart PayPal an. PayPal ist ein Zahlungsdienstleister, bei dem
        mittels virtueller Konten Zahlungen abgewickelt werden können. Bei der Zahlung über PayPal
        meldest du dich entweder mit deinem PayPal-Konto bei PayPal an oder nutzt eine andere, dir von
        PayPal angebotene Zahlungsmethode.
      </Typography>

      <Typography variant="body1" paragraph>
        Wenn du während des Bestellvorgangs PayPal als Zahlungsart auswählst und den Kauf abschließt,
        übermitteln wir deinen Vor- und Zunamen, deine E-Mail-Adresse, die Details zu deiner Bestellung
        sowie die IP-Adresse an PayPal.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 b) DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Neben der Abwicklung der Zahlung dienen die übermittelten Daten auch der Bonitätsprüfung und
        Betrugsprävention. PayPal übermittelt deine personenbezogenen Daten für diese Zwecke ggf. an
        Wirtschaftsauskunfteien, externe Dienstleister oder verbundene Unternehmen weiter, insofern dies
        zur Leistungserbringung erforderlich ist.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Datenverarbeitung zur Bonitätsprüfung und Betrugsprävention ist Art. 6
        Abs. 1 S. 1 f) DSGVO (berechtigtes Interesse).
      </Typography>

      <Typography variant="body1" paragraph>
        Die Europäische Betreibergesellschaft von PayPal ist PayPal (Europe) S.à r.l. et Cie, S.C.A.
        22-24 Boulevard Royal, 2449 Luxemburg, Luxemburg.
      </Typography>

      <Typography variant="body1" paragraph>
        Nähere Informationen zur Datenverarbeitung durch PayPal findest du unter{" "}
        <InlineLink
          href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.paypal.com/de/webapps/mpp/ua/privacy-full
        </InlineLink>
      </Typography>

      <Typography variant="h4">Klarna Sofortüberweisung</Typography>

      <Typography variant="body1" paragraph>
        Unser Webshop bietet als Zahlungsart Sofortüberweisung an. Sofortüberweisung ist ein Zahlungsart,
        die eine bargeldlose Zahlung von Waren ermöglicht.
      </Typography>

      <Typography variant="body1" paragraph>
        Wenn du diese Zahlungsart wählst, stellt dir die SOFORT GmbH ihren Zahlungsdienst unmittelbar
        selbst zur Verfügung. Bei der Nutzung von Sofortüberweisung beauftragts du die SOFORT GmbH
        unmittelbar mit der Einstellung der Überweisung in dein Online-Banking Portal. Die SOFORT GmbH
        tritt dir über die Bezahlmaske unmittelbar gegenüber und hat dadurch eine direkte Beziehung zu
        dir. Für die dabei erfolgende Erhebung und Verarbeitung personenbezogener Daten ist mithin die
        SOFORT GmbH selbst verantwortlich.
      </Typography>

      <Typography variant="body1" paragraph>
        Wir erhalten anschließend eine unmittelbare Zahlungsbestätigung, die es uns ermöglicht, die von
        dir bestellte Ware direkt in den Versand zu geben.
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für den Datenaustausch zwischen der SOFORT GmbH und uns ist Art. 6 Abs. 1 S. 1 b)
        DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Betreiber des Dienstes Sofortüberweisung ist die SOFORT GmbH, Theresienhöhe 12, 80339 München,
        Deutschland.
      </Typography>

      <Typography variant="body1" paragraph>
        Nähere Informationen zur Datenverarbeitung durch die SOFORT GmbH findest du unter{" "}
        <InlineLink
          href="https://www.sofort.com/payment/wizard/getCmsContent/data_protection/DE/0/de"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.sofort.com/payment/wizard/getCmsContent/data_protection/DE/0/de
        </InlineLink>
        .
      </Typography>

      <Typography variant="h4">Kreditkarte</Typography>

      <Typography variant="body1" paragraph>
        Unser Webshop bietet als Zahlungsart Kreditkarte an. Wir wickeln Kreditkartenzahlungen mit dem
        Zahlungsdienstleister Stripe ab. Bei der Zahlung mit Kreditkarte übermittelst du Nummer,
        Ablaufdatum und Sicherheitscode deiner Kreditkarte an die Firma Stripe Payments Europe Ltd.
      </Typography>

      <Typography variant="body1" paragraph>
        Mit Hilfe eines technischen Verfahrens wird die Gültigkeit deiner Kreditkartendaten überprüft und
        die Zahlung durchgeführt. Wir erhalten anschließend eine unmittelbare Zahlungsbestätigung, die es
        uns ermöglicht, die von dir bestellte Ware direkt in den Versand zu geben.
      </Typography>

      <Typography variant="body1" paragraph>
        Wenn du während des Bestellvorgangs Kreditkarte als Zahlungsart auswählst und den Kauf
        abschließt, übermitteln wir deinen Vor- und Zunamen, deine E-Mail-Adresse, deine Rechnungs- und
        Lieferanschrift, die Details deiner Bestellung sowie die IP-Adresse an Stripe. Stripe setzt zur
        Unterstützung der Zahlungsabwicklung ein Cookie ein (essenzielles Cookie).
      </Typography>

      <Typography variant="body1" paragraph>
        Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 b) DSGVO (Vertragserfüllung).
      </Typography>

      <Typography variant="body1" paragraph>
        Neben der Abwicklung der Zahlung dienen die übermittelten Daten auch der Bonitätsprüfung und
        Betrugsprävention. Stripe übermittelt deine personenbezogenen Daten für diese Zwecke ggf. an
        Wirtschaftsauskunfteien, externe Dienstleister oder verbundene Unternehmen weiter, insofern dies
        zur Leistungserbringung erforderlich ist.
      </Typography>

      <Typography variant="body1" paragraph>
        Betreiber des Dienstes Stripe ist die Stripe Payments Europe Ltd., 1 Grand Canal Street, Lower
        Grand Canal Dock, Dublin, Ireland.
      </Typography>

      <Typography variant="body1" paragraph>
        Nähere Informationen zur Datenverarbeitung durch Stripe findest du unter
        <InlineLink
          href="https://stripe.com/de/privacy#translation"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://stripe.com/de/privacy#translation
        </InlineLink>
        .
      </Typography>

      <Typography variant="body1" paragraph>
        Die Datenverarbeitung zur Bonitätsprüfung und Betrugsprävention erfolgt auf Basis von Art. 6 Abs.
        1 S. 1 f) DSGVO (berechtigtes Interesse).
      </Typography>

      <Typography variant="h3">4.4 Kundenbetreuung und Support</Typography>

      <Typography variant="body1" paragraph>
        Für die Kundenbetreuung kannst du uns per E-Mail kontaktieren. Eine entsprechende E-Mail-Adresse
        weisen wir zu diesem Zweck an entsprechenden Stellen auf den Seiten unseres Webshops aus. Die
        personenbezogenen Daten, die du uns bei einer Anfrage per E-Mail mitteilst (z. B. Name,
        E-Mail-Adresse, Telefonnummer) werden ausschließlich zur Korrespondenz mit dir und nur für den
        Zweck verarbeitet, zu dem du uns die Daten zur Verfügung gestellt hast.
      </Typography>

      <Typography variant="body1" paragraph>
        Darüber hinaus stellen wir unter https://www.loesung.akte-mord.de/ eine Möglichkeit zur
        Verfügung, zusätzliches Material für unsere verkauften Waren herunterladen zu können und deine
        Lösung zu überprüfen. Um von dieser Möglichkeit Gebrauch zu machen, benötigst du einen
        Zugangscode. Dieser Code liegt jedem unserer Produkte bei und soll sicherstellen, dass lediglich
        Besitzer unserer Produkte von dieser Möglichkeit Gebrauch machen können. Ein Rückschluss vom
        Zugangscode auf deine Person ist nicht möglich und nicht beabsichtigt.
      </Typography>

      <Typography variant="body1" paragraph>
        Die Rechtsgrundlagen für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 b) DSGVO
        (Vertragserfüllung).
      </Typography>

      <Typography variant="h2">5. Deine Rechte</Typography>

      <Typography variant="body1" paragraph>
        Die folgend aufgeführten Rechte kannst du uns gegenüber geltend machen. Bitte sende uns dafür
        eine E-Mail an mail@akte-mord.de. Bitte beachte, dass wir zur Geltendmachung deiner Rechte deine
        Identität überprüfen und feststellen müssen.
      </Typography>

      <Typography variant="h4" component="h3">
        Widerrufsrecht
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, deine Einwilligung zur Datenverarbeitung jederzeit zu widerrufen. Dieses Recht
        gilt mit Wirkung für die Zukunft. Die bis zur Rechtskraft des Widerrufs erhobenen Daten bleiben
        hiervon unberührt.
      </Typography>

      <Typography variant="h4" component="h3">
        Recht auf Datenübertragbarkeit
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, eine Übertragung deiner Daten von uns auf eine andere Stelle zu beantragen.
      </Typography>

      <Typography variant="h4" component="h3">
        Recht auf Auskunft
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, Auskunft über deine Daten zu verlangen.
      </Typography>

      <Typography variant="h4" component="h3">
        Recht auf Berichtigung, Löschung oder Sperrung
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, deine Daten berichtigen, löschen oder sperren zu lassen. Letzteres kommt zur
        Anwendung, wenn die gesetzliche Lage eine Löschung nicht zulässt.
      </Typography>

      <Typography variant="h4" component="h3">
        Recht auf Einschränkung der Verarbeitung
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, von uns die Einschränkung der Verarbeitung zu verlangen.
      </Typography>

      <Typography variant="h4" component="h3">
        Beschwerderecht
      </Typography>
      <Typography variant="body1" paragraph>
        Du hast das Recht, dich zu beschweren, insofern du einen Grund zur Beanstandung haben solltest.
      </Typography>

      <Typography variant="body1" paragraph>
        Du hast darüber hinaus das Recht, dich mit deinem Anliegen an die zuständige Aufsichtsbehörde zu
        richten. Das ist die Berliner Beauftragte für Datenschutz und Informationsfreiheit, Friedrichstr.
        219, 10969 Berlin, Tel.: +49 30 13889-0, Fax: +49 30 2155050, E-Mail:{" "}
        <InlineLink href="mailto:mailbox@datenschutz-berlin.de">
          mailbox@datenschutz-berlin.de
        </InlineLink>
        .
      </Typography>

      <Typography variant="h2">6. Änderung der Datenschutzerklärung</Typography>

      <Typography variant="body1" paragraph>
        Wir als Verantwortliche behalten es uns vor, die Datenschutzerklärung jederzeit an geänderte
        Verarbeitungen und die geltenden Datenschutzbestimmungen anzupassen. Derzeitiger Stand ist
        Oktober 2021.
      </Typography>
    </Container>
  );
};

PrivacyPolicy.getLayout = (page) => (
  <BaseLayout
    title="Datenschutzerklärung | Akte Mord® Cold Cases für Zuhause"
    description="Informationen welche personenbezogenen Daten wir bei der Nutzung unseres Online-Shops erheben und verarbeiten."
    noindex={true}
  >
    {page}
  </BaseLayout>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale ? await serverSideTranslations(locale, ["c-common"], i18nConfig) : {}),
    },
  };
};

export default PrivacyPolicy;
